import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../components/ui/resizable';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Send, Bot, Loader2, Sparkles, Eye, CheckCircle2, XCircle,
  Search, PenTool, Code, ShieldCheck, ThumbsUp, RotateCcw,
  Mail, AlertTriangle, ArrowRight, FileText, ExternalLink,
  Monitor, Smartphone, RefreshCw, Maximize2, Info, Copy, CodeXml,
  Clock, Zap, ChevronRight, Paperclip, SmilePlus, Mic
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import {
  sendChatMessage, getChatHistory, getCampaign,
  continueCampaign, reviseCampaign, approveCampaign, sendTestEmail,
  getLists, updateCampaign, sendCampaign
} from '../lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const STEP_META = {
  research:   { label: 'Research',    icon: Search,      color: '#3b82f6' },
  brainstorm: { label: 'Brainstorm',  icon: Sparkles,    color: '#a855f7' },
  content:    { label: 'Content',     icon: PenTool,     color: '#10b981' },
  html:       { label: 'HTML',        icon: Code,        color: '#f59e0b' },
  review:     { label: 'CXO Review',  icon: ShieldCheck, color: '#ef4444' },
};

// ─── Pipeline Status Row ────────────────────────────────────────────
function PipelineRow({ pipeline }) {
  if (!pipeline) return null;
  const steps = Object.entries(pipeline).filter(([_, v]) => typeof v === 'object' && v.status);
  if (steps.length === 0) return null;
  return (
    <div className="flex items-center gap-1 px-4 py-2 border-b border-border/30 overflow-x-auto" data-testid="pipeline-row">
      {steps.map(([key, step], idx) => {
        const meta = STEP_META[key] || { label: key, icon: Bot, color: '#6b7280' };
        const Icon = meta.icon;
        const isRunning = step.status === 'running';
        const isDone = step.status === 'success' || step.status === 'completed';
        const isError = step.status === 'error';
        return (
          <React.Fragment key={key}>
            <div
              data-testid={`pipeline-chip-${key}`}
              className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium whitespace-nowrap transition-all duration-200 ${
                isRunning ? 'bg-[hsl(var(--accent)/0.1)] text-[hsl(var(--accent))]' :
                isDone ? 'bg-emerald-500/10 text-emerald-400' :
                isError ? 'bg-destructive/10 text-destructive' :
                'bg-secondary/30 text-muted-foreground/60'
              }`}
            >
              {isRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
              {meta.label}
              {isDone && <CheckCircle2 className="w-3 h-3" />}
              {isError && <XCircle className="w-3 h-3" />}
            </div>
            {idx < steps.length - 1 && (
              <ArrowRight className="w-3 h-3 text-muted-foreground/30 flex-shrink-0" />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Rich Card Configurations ──────────────────────────────────────
const CARD_CONFIG = {
  research: {
    badge: 'INTEL REPORT',
    badgeColor: 'hsl(210 90% 55%)',
    borderColor: 'hsl(210 90% 55% / 0.2)',
    bgColor: 'hsl(210 90% 55% / 0.04)',
  },
  brainstorm: {
    badge: 'ARCHITECTURE',
    badgeColor: 'hsl(270 70% 60%)',
    borderColor: 'hsl(270 70% 60% / 0.2)',
    bgColor: 'hsl(270 70% 60% / 0.04)',
  },
  content: {
    badge: 'CONTENT DRAFT',
    badgeColor: 'hsl(160 84% 39%)',
    borderColor: 'hsl(160 84% 39% / 0.2)',
    bgColor: 'hsl(160 84% 39% / 0.04)',
  },
  html: {
    badge: 'BUILD COMPLETE',
    badgeColor: 'hsl(38 92% 50%)',
    borderColor: 'hsl(38 92% 50% / 0.2)',
    bgColor: 'hsl(38 92% 50% / 0.04)',
  },
  review: {
    badge: 'CXO ASSESSMENT',
    badgeColor: 'hsl(0 72% 55%)',
    borderColor: 'hsl(0 72% 55% / 0.2)',
    bgColor: 'hsl(0 72% 55% / 0.04)',
  },
};

// ─── Parse markdown-like text into structured sections ──────────────
function parseSections(text) {
  if (!text) return [];
  const sections = [];
  let currentSection = null;

  text.split('\n').forEach(line => {
    if (line.startsWith('# ') && !line.startsWith('## ')) {
      currentSection = { title: line.slice(2).trim(), level: 1, items: [] };
      sections.push(currentSection);
    } else if (line.startsWith('## ')) {
      currentSection = { title: line.slice(3).trim(), level: 2, items: [] };
      sections.push(currentSection);
    } else if (line.startsWith('### ')) {
      currentSection = { title: line.slice(4).trim(), level: 3, items: [] };
      sections.push(currentSection);
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      const item = line.slice(2).trim();
      if (currentSection) {
        currentSection.items.push(item);
      } else {
        currentSection = { title: '', level: 0, items: [item] };
        sections.push(currentSection);
      }
    } else if (line.trim() && currentSection) {
      currentSection.items.push(line.trim());
    }
  });
  return sections;
}

// ─── Rich inline text renderer ──────────────────────────────────────
function renderInlineText(text) {
  if (!text) return text;
  let html = text;
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong class="text-foreground font-semibold">$1</strong>');
  html = html.replace(/`([^`]+)`/g, '<code class="text-[hsl(var(--brand))] bg-white/5 px-1.5 py-0.5 rounded text-[11px] font-mono border border-white/5">$1</code>');
  return html;
}

// ─── Research Rich Card ─────────────────────────────────────────────
function ResearchCard({ content, data }) {
  const sections = parseSections(content);
  const threatLevel = content?.match(/Threat Level:\s*(\w+)/i)?.[1] || data?.threat_level;
  const topic = data?.topic || content?.match(/Topic:\s*(.+)/i)?.[1] || '';
  const threatColor = (threatLevel || '').toLowerCase() === 'critical' ? 'hsl(0 72% 55%)' :
    (threatLevel || '').toLowerCase() === 'high' ? 'hsl(24 95% 64%)' :
    (threatLevel || '').toLowerCase() === 'medium' ? 'hsl(38 92% 50%)' : 'hsl(160 84% 39%)';

  return (
    <div className="space-y-3" data-testid="research-rich-card">
      {/* Threat assessment strip */}
      {threatLevel && (
        <div className="flex items-center gap-3 p-3 rounded-lg border border-white/5 bg-white/[0.02]">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" style={{ color: threatColor }} />
            <span className="text-[10px] font-semibold uppercase tracking-[0.16em]" style={{ color: threatColor }}>
              {threatLevel} SEVERITY
            </span>
          </div>
          {topic && <span className="text-xs text-muted-foreground truncate flex-1 ml-2 border-l border-white/10 pl-3">{topic}</span>}
        </div>
      )}
      {/* Sections */}
      {sections.filter(s => s.title || s.items.length > 0).map((section, idx) => (
        <div key={idx}>
          {section.title && section.level <= 2 && (
            <p className="text-xs font-semibold text-foreground mb-1.5" dangerouslySetInnerHTML={{ __html: renderInlineText(section.title) }} />
          )}
          {section.title && section.level > 2 && (
            <p className="text-[11px] font-medium text-muted-foreground mb-1" dangerouslySetInnerHTML={{ __html: renderInlineText(section.title) }} />
          )}
          {section.items.length > 0 && (
            <ul className="space-y-1">
              {section.items.map((item, i) => (
                <li key={i} className="flex gap-2 text-sm leading-relaxed text-foreground/85">
                  <span className="w-1.5 h-1.5 rounded-full bg-[hsl(210_90%_55%/0.5)] mt-2 flex-shrink-0" />
                  <span dangerouslySetInnerHTML={{ __html: renderInlineText(item) }} />
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
      {sections.length === 0 && content && (
        <div className="text-sm text-foreground/85 leading-relaxed whitespace-pre-wrap">{content}</div>
      )}
    </div>
  );
}

// ─── Brainstorm Rich Card (Email Architecture) ──────────────────────
function BrainstormCard({ content }) {
  const sections = parseSections(content);
  const subjectLines = sections.find(s => s.title?.toLowerCase().includes('subject'));
  const previewText = sections.find(s => s.title?.toLowerCase().includes('preview'));
  const otherSections = sections.filter(s =>
    !s.title?.toLowerCase().includes('subject') &&
    !s.title?.toLowerCase().includes('preview') &&
    !s.title?.toLowerCase().includes('email content architecture') &&
    (s.title || s.items.length > 0)
  );

  return (
    <div className="space-y-3" data-testid="brainstorm-rich-card">
      {/* Subject Line Options — highlighted */}
      {subjectLines && subjectLines.items.length > 0 && (
        <div className="p-3 rounded-lg bg-[hsl(270_70%_60%/0.06)] border border-[hsl(270_70%_60%/0.12)]">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[hsl(270_70%_60%)] mb-2">Subject Line Options</p>
          <div className="space-y-1.5">
            {subjectLines.items.map((item, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-[hsl(270_70%_60%/0.15)] text-[10px] font-bold text-[hsl(270_70%_60%)] flex-shrink-0 mt-0.5">
                  {String.fromCharCode(65 + i)}
                </span>
                <span className="text-foreground/90 leading-relaxed" dangerouslySetInnerHTML={{ __html: renderInlineText(item.replace(/^Option [A-Z]:\s*/i, '')) }} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Preview Text */}
      {previewText && previewText.items.length > 0 && (
        <div className="flex items-start gap-2 p-2.5 rounded-lg bg-white/[0.02] border border-white/5">
          <Mail className="w-3.5 h-3.5 text-muted-foreground/60 flex-shrink-0 mt-0.5" />
          <span className="text-xs text-muted-foreground leading-relaxed italic">{previewText.items[0]}</span>
        </div>
      )}

      {/* Content structure sections */}
      {otherSections.map((section, idx) => (
        <div key={idx} className="pl-0">
          {section.title && (
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-5 h-5 rounded bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                <span className="text-[9px] font-bold text-muted-foreground">{idx + 1}</span>
              </div>
              <p className="text-xs font-semibold text-foreground" dangerouslySetInnerHTML={{ __html: renderInlineText(section.title) }} />
            </div>
          )}
          {section.items.length > 0 && (
            <ul className="space-y-0.5 ml-7">
              {section.items.slice(0, 5).map((item, i) => (
                <li key={i} className="text-[13px] text-foreground/75 leading-relaxed" dangerouslySetInnerHTML={{ __html: '&bull; ' + renderInlineText(item) }} />
              ))}
              {section.items.length > 5 && (
                <li className="text-[11px] text-muted-foreground/60 italic">+{section.items.length - 5} more items</li>
              )}
            </ul>
          )}
        </div>
      ))}

      {sections.length === 0 && content && (
        <div className="text-sm text-foreground/85 leading-relaxed whitespace-pre-wrap">{content}</div>
      )}
    </div>
  );
}

// ─── Content Draft Rich Card ────────────────────────────────────────
function ContentCard({ content }) {
  const sections = parseSections(content);
  const subjectMatch = content?.match(/Subject:\s*(.+)/i);
  const preheaderMatch = content?.match(/Preheader:\s*(.+)/i);

  return (
    <div className="space-y-3" data-testid="content-rich-card">
      {/* Subject + Preheader highlight */}
      {(subjectMatch || preheaderMatch) && (
        <div className="p-3 rounded-lg bg-[hsl(160_84%_39%/0.06)] border border-[hsl(160_84%_39%/0.12)]">
          {subjectMatch && (
            <div className="mb-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[hsl(160_84%_39%)] mb-1">Subject Line</p>
              <p className="text-sm font-medium text-foreground" dangerouslySetInnerHTML={{ __html: renderInlineText(subjectMatch[1].trim()) }} />
            </div>
          )}
          {preheaderMatch && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/60 mb-0.5">Preheader</p>
              <p className="text-xs text-muted-foreground italic">{preheaderMatch[1].trim()}</p>
            </div>
          )}
        </div>
      )}
      {/* Body sections */}
      {sections.filter(s => s.title && !s.title.toLowerCase().includes('content generated')).map((section, idx) => (
        <div key={idx}>
          <p className="text-xs font-semibold text-foreground mb-1" dangerouslySetInnerHTML={{ __html: renderInlineText(section.title) }} />
          {section.items.length > 0 && (
            <ul className="space-y-0.5">
              {section.items.map((item, i) => (
                <li key={i} className="text-[13px] text-foreground/80 leading-relaxed pl-3 border-l-2 border-[hsl(160_84%_39%/0.2)]" dangerouslySetInnerHTML={{ __html: renderInlineText(item) }} />
              ))}
            </ul>
          )}
        </div>
      ))}
      {sections.length === 0 && content && (
        <div className="text-sm text-foreground/85 leading-relaxed whitespace-pre-wrap">{content}</div>
      )}
    </div>
  );
}

// ─── CXO Review Rich Card ───────────────────────────────────────────
function ReviewCard({ content, data }) {
  const scoreMatch = content?.match(/Score:\s*(\d+\.?\d*)/);
  const score = scoreMatch ? parseFloat(scoreMatch[1]) : data?.overall_score;
  const riskMatch = content?.match(/Risk Level:\s*(\w+)/i);
  const risk = riskMatch ? riskMatch[1] : data?.risk_level;
  const approvedMatch = content?.match(/Approved:\s*(Yes|No|true|false)/i);
  const approved = approvedMatch ? (approvedMatch[1].toLowerCase() === 'yes' || approvedMatch[1].toLowerCase() === 'true') : data?.approved;

  // Score is on a 0-10 scale; convert to percentage for the gauge
  const scorePercent = score != null ? (score <= 10 ? score * 10 : score) : 0;
  const scoreDisplay = score != null ? (score <= 10 ? score.toFixed(1) : score) : '—';
  const scoreMax = score != null && score <= 10 ? '10' : '100';
  const scoreColor = scorePercent >= 80 ? 'hsl(160 84% 39%)' : scorePercent >= 60 ? 'hsl(38 92% 50%)' : 'hsl(0 72% 55%)';
  const sections = parseSections(content);
  const strengthsSection = sections.find(s => s.title?.toLowerCase().includes('strength'));
  const concernsSection = sections.find(s => s.title?.toLowerCase().includes('concern'));
  const recMatch = content?.match(/Recommendation:\s*(.+)/i);

  return (
    <div className="space-y-3" data-testid="review-rich-card">
      {/* Score + Risk + Approval header */}
      <div className="flex items-center gap-4 p-3 rounded-lg bg-white/[0.02] border border-white/5">
        {score != null && (
          <div className="flex items-center gap-3">
            <div className="relative w-12 h-12 flex items-center justify-center">
              <svg className="w-12 h-12 -rotate-90" viewBox="0 0 36 36">
                <circle cx="18" cy="18" r="15.9155" fill="none" stroke="hsl(var(--secondary))" strokeWidth="2.5" />
                <circle cx="18" cy="18" r="15.9155" fill="none" stroke={scoreColor} strokeWidth="2.5"
                  strokeDasharray={`${scorePercent} ${100 - scorePercent}`} strokeLinecap="round" />
              </svg>
              <span className="absolute text-xs font-bold" style={{ color: scoreColor }}>{scoreDisplay}</span>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground/60">Score</p>
              <p className="text-sm font-semibold text-foreground">{scoreDisplay}/{scoreMax}</p>
            </div>
          </div>
        )}
        <div className="w-px h-8 bg-white/10" />
        {risk && (
          <div>
            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground/60">Risk</p>
            <p className="text-sm font-semibold text-foreground">{risk}</p>
          </div>
        )}
        <div className="ml-auto">
          <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider ${
            approved
              ? 'bg-[hsl(160_84%_39%/0.1)] text-[hsl(160_84%_39%)] border border-[hsl(160_84%_39%/0.2)]'
              : 'bg-[hsl(38_92%_50%/0.1)] text-[hsl(38_92%_50%)] border border-[hsl(38_92%_50%/0.2)]'
          }`}>
            {approved ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
            {approved ? 'Approved' : 'Needs Review'}
          </span>
        </div>
      </div>

      {/* Strengths — from structured data or parsed text */}
      {(data?.strengths || (strengthsSection && strengthsSection.items.length > 0)) && (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[hsl(160_84%_39%)] mb-1.5">Strengths</p>
          <ul className="space-y-1">
            {(data?.strengths || strengthsSection?.items || []).map((item, i) => (
              <li key={i} className="flex gap-2 text-[13px] text-foreground/80 leading-relaxed">
                <CheckCircle2 className="w-3.5 h-3.5 text-[hsl(160_84%_39%/0.6)] flex-shrink-0 mt-0.5" />
                <span dangerouslySetInnerHTML={{ __html: renderInlineText(item) }} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Concerns — from structured data or parsed text */}
      {(data?.concerns || (concernsSection && concernsSection.items.length > 0)) && (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[hsl(38_92%_50%)] mb-1.5">Concerns</p>
          <ul className="space-y-1">
            {(data?.concerns || concernsSection?.items || []).map((item, i) => (
              <li key={i} className="flex gap-2 text-[13px] text-foreground/80 leading-relaxed">
                <AlertTriangle className="w-3.5 h-3.5 text-[hsl(38_92%_50%/0.6)] flex-shrink-0 mt-0.5" />
                <span dangerouslySetInnerHTML={{ __html: renderInlineText(item) }} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recommendation */}
      {recMatch && (
        <div className="p-2.5 rounded-lg border border-white/5 bg-white/[0.02]">
          <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground/60 mb-1">Recommendation</p>
          <p className="text-sm text-foreground/85 leading-relaxed" dangerouslySetInnerHTML={{ __html: renderInlineText(recMatch[1].trim()) }} />
        </div>
      )}
    </div>
  );
}

// ─── Pipeline Action Button (Unified Status/Approval) ───────────────
function PipelineActionButton({ campaign, onApprove, onRevise, onFinalize }) {
  const status = campaign?.status;
  const pipeline = campaign?.pipeline || {};

  // Determine what step is currently running
  const runningStep = ['research', 'brainstorm', 'content', 'html', 'review'].find(
    s => pipeline[s]?.status === 'running'
  );
  // Determine the last completed step
  const completedSteps = ['research', 'brainstorm', 'content', 'html', 'review'].filter(
    s => pipeline[s]?.status === 'success'
  );
  const lastCompleted = completedSteps[completedSteps.length - 1];

  const stepLabels = {
    research: 'Research Agent',
    brainstorm: 'Brainstorm Architect',
    content: 'Content Writer',
    html: 'HTML Builder',
    review: 'CXO Reviewer',
  };
  const nextStepLabels = {
    research: 'Brainstorm',
    brainstorm: 'Content Writer',
    content: 'HTML Builder',
    html: 'CXO Review',
    review: 'Finalize',
  };

  // ─── RUNNING STATE: spinner + step name ───
  if (status === 'pipeline_running' && runningStep) {
    return (
      <div
        className="h-10 inline-flex items-center gap-2.5 rounded-full px-5 border border-[hsl(var(--brand)/0.25)] bg-[hsl(var(--brand)/0.08)]"
        data-testid="pipeline-action-btn-running"
      >
        <Loader2 className="w-4 h-4 text-[hsl(var(--brand))] animate-spin" />
        <span className="text-sm font-medium text-[hsl(var(--brand))]">
          {stepLabels[runningStep] || runningStep} Processing...
        </span>
      </div>
    );
  }

  // ─── AWAITING BRAINSTORM APPROVAL (content_review) ───
  if (status === 'content_review') {
    return (
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onRevise}
          className="h-10 rounded-full text-sm gap-1.5 border-white/10 px-4"
          data-testid="pipeline-action-btn-revise"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Revise
        </Button>
        <Button
          onClick={onApprove}
          className="h-10 rounded-full text-sm font-semibold gap-2 px-5 bg-[hsl(var(--accent-warm))] text-[hsl(var(--accent-warm-foreground))] hover:bg-[hsl(var(--accent-warm)/0.88)] active:scale-[0.98] transition-transform duration-150"
          data-testid="pipeline-action-btn-approve"
        >
          <CheckCircle2 className="w-4 h-4" />
          Approve → Content Writer
          <ArrowRight className="w-3.5 h-3.5" />
        </Button>
      </div>
    );
  }

  // ─── AWAITING CONTENT DRAFT APPROVAL (content_draft_review) ───
  if (status === 'content_draft_review') {
    return (
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onRevise}
          className="h-10 rounded-full text-sm gap-1.5 border-white/10 px-4"
          data-testid="pipeline-action-btn-revise-content"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Revise Content
        </Button>
        <Button
          onClick={onApprove}
          className="h-10 rounded-full text-sm font-semibold gap-2 px-5 bg-[hsl(var(--accent-warm))] text-[hsl(var(--accent-warm-foreground))] hover:bg-[hsl(var(--accent-warm)/0.88)] active:scale-[0.98] transition-transform duration-150"
          data-testid="pipeline-action-btn-approve-content"
        >
          <CheckCircle2 className="w-4 h-4" />
          Approve → HTML Builder
          <ArrowRight className="w-3.5 h-3.5" />
        </Button>
      </div>
    );
  }

  // ─── REVIEW NEEDED (all steps done, CXO review complete) ───
  if (status === 'review_needed') {
    return (
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onRevise}
          className="h-10 rounded-full text-sm gap-1.5 border-white/10 px-4"
          data-testid="pipeline-action-btn-revise-final"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Revise
        </Button>
        <Button
          onClick={onApprove}
          className="h-10 rounded-full text-sm font-semibold gap-2 px-5 bg-[hsl(var(--status-success))] text-white hover:bg-[hsl(var(--status-success)/0.88)] active:scale-[0.98] transition-transform duration-150"
          data-testid="pipeline-action-btn-approve-final"
        >
          <CheckCircle2 className="w-4 h-4" />
          Approve Campaign
        </Button>
      </div>
    );
  }

  // ─── APPROVED → Show Finalize button ───
  if (status === 'approved' || status === 'sent' || status === 'scheduled') {
    return (
      <Button
        onClick={onFinalize}
        className="h-10 rounded-full text-sm font-semibold gap-2 px-5 bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))] hover:bg-[hsl(var(--brand)/0.88)] active:scale-[0.98] transition-transform duration-150"
        data-testid="pipeline-action-btn-finalize"
      >
        <Send className="w-4 h-4" />
        Finalize & Send
      </Button>
    );
  }

  // ─── PIPELINE RUNNING (no specific step detected) ───
  if (status === 'pipeline_running') {
    return (
      <div
        className="h-10 inline-flex items-center gap-2.5 rounded-full px-5 border border-[hsl(var(--brand)/0.25)] bg-[hsl(var(--brand)/0.08)]"
        data-testid="pipeline-action-btn-running-generic"
      >
        <Loader2 className="w-4 h-4 text-[hsl(var(--brand))] animate-spin" />
        <span className="text-sm font-medium text-[hsl(var(--brand))]">Pipeline Running...</span>
      </div>
    );
  }

  // ─── IDLE/DRAFT/OTHER ───
  return null;
}

// ─── Chat Message ───────────────────────────────────────────────────
function ChatMsg({ msg, onOpenPreview }) {
  const isUser = msg.role === 'user';
  const meta = msg.agent_step ? STEP_META[msg.agent_step] : null;
  const AgentIcon = meta?.icon || Bot;
  const isRunning = msg.status === 'running';
  const isError = msg.status === 'error';
  const timestamp = msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
  const cardCfg = msg.agent_step ? CARD_CONFIG[msg.agent_step] : null;

  // ─── User message (right-aligned) ───
  if (isUser) {
    return (
      <div className="flex justify-end px-5 py-3" data-testid="chat-msg-user">
        <div className="flex flex-col items-end max-w-[78%]">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[10px] font-mono uppercase tracking-[0.12em] text-muted-foreground">
              Administrator {timestamp && <span className="ml-1 opacity-60">• {timestamp}</span>}
            </span>
            <div className="w-6 h-6 rounded-full bg-[hsl(var(--secondary))] border border-white/10 flex items-center justify-center">
              <svg className="w-3 h-3 text-muted-foreground" fill="currentColor" viewBox="0 0 20 20"><path d="M10 10a4 4 0 100-8 4 4 0 000 8zm-7 8a7 7 0 0114 0H3z" /></svg>
            </div>
          </div>
          <div className="bg-[hsl(var(--secondary)/0.6)] border border-white/10 rounded-2xl rounded-tr-md px-4 py-3" style={{ boxShadow: '0 1px 0 hsl(0 0% 0% / 0.35)' }}>
            <p className="text-sm leading-relaxed text-foreground whitespace-pre-wrap">{msg.content}</p>
          </div>
        </div>
      </div>
    );
  }

  // ─── Agent message (left-aligned, Rich Card style) ───
  return (
    <div className="flex gap-3 px-5 py-3" data-testid={`chat-msg-${msg.agent_step || 'agent'}`}>
      {/* Avatar with ring */}
      <div className="flex-shrink-0 mt-1">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center ring-2 ring-offset-2 ring-offset-background relative"
          style={{ backgroundColor: `${meta?.color || '#6b7280'}20`, ringColor: `${meta?.color || '#6b7280'}40` }}
        >
          <AgentIcon className="w-4 h-4" style={{ color: meta?.color || '#6b7280' }} />
          {isRunning && <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full animate-pulse border-2 border-background" style={{ backgroundColor: meta?.color }} />}
        </div>
      </div>

      <div className="flex-1 min-w-0 max-w-[85%]">
        {/* Meta line: AGENT NAME • BADGE • TIMESTAMP */}
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[10px] font-mono uppercase tracking-[0.12em] text-muted-foreground">
            {msg.agent_name || meta?.label || 'Campaign Architect'}
            {timestamp && <span className="ml-1 opacity-60">• {timestamp}</span>}
          </span>
          {cardCfg && !isRunning && (
            <span
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em]"
              style={{ color: cardCfg.badgeColor, backgroundColor: `${cardCfg.badgeColor}15`, border: `1px solid ${cardCfg.badgeColor}25` }}
              data-testid={`card-badge-${msg.agent_step}`}
            >
              {cardCfg.badge}
            </span>
          )}
          {isRunning && <span className="flex items-center gap-1 text-[10px] text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" /> Processing...</span>}
        </div>

        {/* Message card — rich or standard */}
        {(msg.content || isRunning) && (
          <div
            className="rounded-2xl rounded-tl-md px-4 py-3.5 text-sm leading-relaxed overflow-hidden"
            style={{
              boxShadow: '0 1px 0 hsl(0 0% 0% / 0.35)',
              borderLeft: cardCfg && !isRunning ? `3px solid ${cardCfg.borderColor}` : undefined,
              backgroundColor: isError ? undefined : (cardCfg && !isRunning ? cardCfg.bgColor : undefined),
              border: isError ? '1px solid hsl(0 72% 55% / 0.2)' : (!cardCfg || isRunning ? '1px solid hsl(var(--border) / 0.3)' : undefined),
            }}
          >
            {isRunning ? (
              <div className="space-y-2">
                <div className="h-3 bg-secondary/50 rounded w-3/4 animate-pulse" />
                <div className="h-3 bg-secondary/30 rounded w-1/2 animate-pulse" />
                <div className="h-3 bg-secondary/20 rounded w-2/3 animate-pulse" />
              </div>
            ) : msg.agent_step === 'research' ? (
              <ResearchCard content={msg.content} data={msg.structured_data} />
            ) : msg.agent_step === 'brainstorm' ? (
              <BrainstormCard content={msg.content} />
            ) : msg.agent_step === 'content' ? (
              <ContentCard content={msg.content} />
            ) : msg.agent_step === 'review' ? (
              <ReviewCard content={msg.content} data={msg.structured_data} />
            ) : (
              <div className="space-y-0.5 text-foreground/90 whitespace-pre-wrap">
                {msg.content?.split('\n').map((line, i) => {
                  let html = line;
                  html = html.replace(/\*\*(.+?)\*\*/g, '<strong class="text-foreground font-semibold">$1</strong>');
                  html = html.replace(/`([^`]+)`/g, '<code class="text-[hsl(var(--brand))] bg-white/5 px-1 rounded text-[11px]">$1</code>');
                  if (html.startsWith('- ')) return <li key={i} className="ml-4 text-sm text-muted-foreground/90 list-disc" dangerouslySetInnerHTML={{ __html: html.slice(2) }} />;
                  if (html.startsWith('## ')) return <h3 key={i} className="text-sm font-semibold text-foreground mt-2 mb-0.5" dangerouslySetInnerHTML={{ __html: html.slice(3) }} />;
                  if (html.startsWith('# ')) return <h2 key={i} className="text-sm font-bold text-[hsl(var(--brand))] mt-2 mb-0.5" dangerouslySetInnerHTML={{ __html: html.slice(2) }} />;
                  if (html === '') return <br key={i} />;
                  return <p key={i} className="text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: html }} />;
                })}
              </div>
            )}
          </div>
        )}

        {/* HTML Preview button */}
        {msg.html_preview && (
          <Button variant="outline" size="sm" className="text-xs h-8 gap-1.5 mt-2 rounded-xl border-white/10 bg-white/5" onClick={() => onOpenPreview(msg.html_preview)} data-testid="open-preview-btn">
            <Eye className="w-3.5 h-3.5" /> View Email Preview
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Synthesize messages from pipeline data ────────────────────────
function synthesizeFromPipeline(campaignData) {
  if (!campaignData?.pipeline) return [];
  const msgs = [];
  const pipeline = campaignData.pipeline;
  const STEP_ORDER = ['research', 'brainstorm', 'content', 'html', 'review'];
  const ts = campaignData.created_at || new Date().toISOString();

  for (const step of STEP_ORDER) {
    const stepData = pipeline[step];
    if (!stepData || !stepData.output) continue;
    const output = stepData.output;
    let content = '';
    let htmlPreview = null;

    switch (step) {
      case 'research':
        content = output.executive_summary
          ? `## Research Complete\n\n**Topic:** ${output.topic || ''}\n\n${output.executive_summary}\n\n**Threat Level:** ${output.threat_level || 'N/A'}`
          : JSON.stringify(output).slice(0, 500);
        if (output.key_findings && Array.isArray(output.key_findings)) {
          content += '\n\n**Key Findings:**\n' + output.key_findings.map(f => `- ${f}`).join('\n');
        }
        break;
      case 'brainstorm':
        content = output.markdown_content
          ? output.markdown_content.slice(0, 1200) + (output.markdown_content.length > 1200 ? '\n\n...' : '')
          : 'Brainstorm outline completed.';
        break;
      case 'content':
        content = `## Content Generated\n\n**Subject:** ${output.subject_line || ''}\n\n**Preheader:** ${output.preheader || ''}\n\n${output.opening_hook || ''}`;
        if (output.body_sections && Array.isArray(output.body_sections)) {
          content += '\n\n' + output.body_sections.map(s => `### ${s.heading || ''}\n${s.body || ''}`).join('\n\n');
        }
        break;
      case 'html':
        content = 'HTML email generated successfully. Click "View Email Preview" to see the result.';
        htmlPreview = output.html || null;
        break;
      case 'review':
        content = `## CXO Review\n\n**Score:** ${output.overall_score || 'N/A'}/100\n**Risk Level:** ${output.risk_level || 'N/A'}\n**Approved:** ${output.approved ? 'Yes' : 'No'}`;
        if (output.strengths && Array.isArray(output.strengths)) {
          content += '\n\n**Strengths:**\n' + output.strengths.map(s => `- ${s}`).join('\n');
        }
        if (output.concerns && Array.isArray(output.concerns)) {
          content += '\n\n**Concerns:**\n' + output.concerns.map(c => `- ${c}`).join('\n');
        }
        if (output.recommendation) {
          content += '\n\n**Recommendation:** ' + output.recommendation;
        }
        break;
      default:
        content = typeof output === 'string' ? output : JSON.stringify(output).slice(0, 500);
    }

    msgs.push({
      role: 'agent',
      agent_step: step,
      agent_name: STEP_META[step]?.label || step,
      content,
      structured_data: typeof output === 'object' ? output : null,
      status: stepData.status === 'success' || stepData.status === 'completed' ? 'success' : stepData.status,
      html_preview: htmlPreview,
      timestamp: stepData.completed_at || ts,
    });
  }

  return msgs;
}

// ─── Main CampaignTab Component ─────────────────────────────────────
export default function CampaignTab({ campaignId, initialPrompt }) {
  const [campaign, setCampaign] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const chatScrollRef = useRef(null);
  const pollingRef = useRef(null);
  const lastMsgCountRef = useRef(0);
  const sessionId = useRef(`campaign-${campaignId}`);
  const pipelineSynthesized = useRef(false);

  // ─── Scroll helper ───
  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      const viewport = chatScrollRef.current?.querySelector?.('[data-radix-scroll-area-viewport]');
      if (viewport) {
        viewport.scrollTop = viewport.scrollHeight;
      } else if (scrollRef.current) {
        scrollRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    });
  }, []);

  // ─── Load campaign + synthesize pipeline messages ───
  const loadCampaign = useCallback(async () => {
    if (!campaignId) return;
    try {
      const res = await getCampaign(campaignId);
      const data = res.data;
      setCampaign(data);

      // Merge pipeline synthesis into messages (always, to ensure all steps are visible)
      if (data?.pipeline) {
        const hasCompletedSteps = Object.values(data.pipeline).some(
          s => typeof s === 'object' && s.output && (s.status === 'success' || s.status === 'completed')
        );
        if (hasCompletedSteps) {
          const synth = synthesizeFromPipeline(data);
          if (synth.length > 0) {
            setMessages(prev => {
              // Build a set of step names already in messages
              const existingSteps = new Set(
                prev.filter(m => m.agent_step && (m.status === 'success' || m.status === 'completed'))
                  .map(m => m.agent_step)
              );
              
              // Add any synthesized messages that don't have a matching step
              const toAdd = synth.filter(s => !existingSteps.has(s.agent_step));
              
              if (toAdd.length === 0) return prev;
              
              // Remove any "running" placeholders for steps we're adding
              const addingSteps = new Set(toAdd.map(s => s.agent_step));
              const filtered = prev.filter(m => !(m.role === 'agent' && m.status === 'running' && addingSteps.has(m.agent_step)));
              
              // Merge and sort by step order
              const STEP_ORDER = ['research', 'brainstorm', 'content', 'html', 'review'];
              const merged = [...filtered, ...toAdd];
              merged.sort((a, b) => {
                const aIdx = STEP_ORDER.indexOf(a.agent_step);
                const bIdx = STEP_ORDER.indexOf(b.agent_step);
                if (aIdx >= 0 && bIdx >= 0) return aIdx - bIdx;
                if (aIdx >= 0) return -1;
                if (bIdx >= 0) return 1;
                return 0;
              });
              
              return merged;
            });
            setTimeout(scrollToBottom, 100);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load campaign:', err);
    }
  }, [campaignId, scrollToBottom]);

  // ─── Load chat history ───
  const loadChat = useCallback(async () => {
    if (!campaignId) return;
    try {
      const res = await getChatHistory(sessionId.current);
      const serverMsgs = res.data || [];
      if (serverMsgs.length === 0) return;

      // Only process truly new messages
      if (serverMsgs.length > lastMsgCountRef.current) {
        const newMsgs = serverMsgs.slice(lastMsgCountRef.current);
        lastMsgCountRef.current = serverMsgs.length;

        setMessages(prev => {
          // Build a set of existing message identifiers for dedup
          const existingIds = new Set(prev.map(m => m._id || m.id).filter(Boolean));
          const existingStepStatus = new Set(prev.map(m => m.agent_step ? `${m.agent_step}::${m.status}` : null).filter(Boolean));

          const toAdd = [];
          for (const msg of newMsgs) {
            // Skip if we already have this exact message by ID
            if ((msg._id || msg.id) && existingIds.has(msg._id || msg.id)) continue;

            // For agent messages, skip "running" status if we already have a "success" for that step
            if (msg.role === 'agent' && msg.agent_step && msg.status === 'running') {
              if (existingStepStatus.has(`${msg.agent_step}::success`)) continue;
            }

            // For agent success messages, replace existing running messages for that step
            if (msg.role === 'agent' && msg.agent_step && msg.status === 'success') {
              // Skip if we already have a success for this step (from pipeline synthesis)
              if (existingStepStatus.has(`${msg.agent_step}::success`)) continue;
            }

            toAdd.push({ ...msg, timestamp: msg.timestamp || new Date().toISOString() });
          }

          if (toAdd.length === 0) return prev;

          // Remove "running" placeholders for steps that now have success messages
          const successSteps = new Set(toAdd.filter(m => m.agent_step && m.status === 'success').map(m => m.agent_step));
          const filtered = prev.filter(m => !(m.role === 'agent' && m.status === 'running' && successSteps.has(m.agent_step)));

          return [...filtered, ...toAdd];
        });
        setTimeout(scrollToBottom, 100);
      } else if (serverMsgs.length > 0 && messages.length === 0) {
        // Initial load — no messages yet
        const displayMsgs = serverMsgs.filter(m => m.content && m.content.trim()).map(m => ({ ...m, timestamp: m.timestamp || new Date().toISOString() }));
        setMessages(displayMsgs);
        lastMsgCountRef.current = serverMsgs.length;
        setTimeout(scrollToBottom, 100);
      }
    } catch (err) {
      // Session may not exist yet - that's fine
    }
  }, [campaignId, messages.length, scrollToBottom]);

  // ─── Polling ───
  useEffect(() => {
    if (!campaignId) return;
    loadCampaign();
    loadChat();
    pollingRef.current = setInterval(() => { loadCampaign(); loadChat(); }, 3000);
    return () => clearInterval(pollingRef.current);
  }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Auto-scroll on new messages ───
  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // ─── Send message ───
  const handleSend = async (text) => {
    const msg = text || input.trim();
    if (!msg) return;
    setInput('');
    const userMsg = { role: 'user', content: msg, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setSending(true);
    try {
      await sendChatMessage(msg, sessionId.current, campaignId);
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // ─── Actions ───
  const handleApprove = async () => {
    try {
      const status = campaign?.status;
      if (status === 'content_review') {
        await continueCampaign(campaignId, { action: 'approve' });
        toast.success('Brainstorm approved, pipeline continuing...');
      } else if (status === 'content_draft_review') {
        await continueCampaign(campaignId, { action: 'approve_content' });
        toast.success('Content approved, building HTML...');
      } else if (status === 'review_needed') {
        await approveCampaign(campaignId);
        toast.success('Campaign approved!');
      }
      await loadCampaign();
    } catch (err) {
      toast.error('Action failed');
    }
  };

  const [showReviseDialog, setShowReviseDialog] = useState(false);
  const [reviseFeedback, setReviseFeedback] = useState('');
  
  const [reviseTarget, setReviseTarget] = useState('full');  // 'full', 'html', 'review'
  
  const handleRevise = async (feedback) => {
    // If feedback is not a string (e.g., React event), open the dialog
    if (typeof feedback !== 'string' || !feedback) {
      setShowReviseDialog(true);
      return;
    }
    try {
      const targetMap = {
        'full': ['content', 'html', 'review'],
        'html': ['html', 'review'],
        'review': ['review'],
      };
      await reviseCampaign(campaignId, { 
        instructions: feedback, 
        target_stages: targetMap[reviseTarget] || ['content', 'html', 'review']
      });
      toast.success('Revision requested — pipeline will re-run');
      setShowReviseDialog(false);
      setReviseFeedback('');
      setReviseTarget('full');
      await loadCampaign();
    } catch (err) {
      toast.error('Revision failed');
    }
  };

  const handleSendTest = async () => {
    try {
      await sendTestEmail(campaignId);
      toast.success('Test email sent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    }
  };

  // ─── Save Draft: persist Finalize Transmission config to campaign ───
  const handleSaveDraft = async () => {
    if (!campaignId) return;
    setSavingDraft(true);
    try {
      await updateCampaign(campaignId, {
        subject_line: subjectLine,
        from_name: fromName,
        reply_to: replyTo,
        recipient_list_id: recipientListId,
        send_strategy: sendStrategy,
      });
      toast.success('Draft saved successfully');
      await loadCampaign();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  // ─── Send Campaign: save config first, then dispatch to list ───
  const handleSendCampaign = async () => {
    if (!campaignId) return;
    if (!recipientListId) {
      toast.error('Please select a recipient list before sending');
      return;
    }
    if (!subjectLine.trim()) {
      toast.error('Please enter a subject line before sending');
      return;
    }
    setSendingCampaign(true);
    try {
      // First, save the latest config
      await updateCampaign(campaignId, {
        subject_line: subjectLine,
        from_name: fromName,
        reply_to: replyTo,
        recipient_list_id: recipientListId,
        send_strategy: sendStrategy,
      });
      // Then dispatch
      await sendCampaign(campaignId, {
        campaign_id: campaignId,
        list_id: recipientListId,
        segment_tags: [],
      });
      toast.success('Campaign is being sent!');
      await loadCampaign();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send campaign');
    } finally {
      setSendingCampaign(false);
    }
  };

  const htmlContent = campaign?.pipeline?.html?.output?.html || campaign?.html_content || '';
  const publicUrl = campaignId ? `${BACKEND_URL}/api/public/campaign/${campaignId}` : null;
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [previewMode, setPreviewMode] = useState('preview'); // 'preview' | 'code'
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [viewMode, setViewMode] = useState('chat'); // 'chat' | 'finalize'
  const [subjectLine, setSubjectLine] = useState('');
  const [fromName, setFromName] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [sendStrategy, setSendStrategy] = useState('now');
  const [recipientListId, setRecipientListId] = useState('');
  const [contactLists, setContactLists] = useState([]);
  const [savingDraft, setSavingDraft] = useState(false);
  const [sendingCampaign, setSendingCampaign] = useState(false);

  // Load contact lists for recipient selector
  useEffect(() => {
    const fetchLists = async () => {
      try {
        const res = await getLists();
        setContactLists(res.data || []);
      } catch (err) {
        console.error('Failed to load contact lists:', err);
      }
    };
    fetchLists();
  }, []);

  // Sync fields from campaign data
  useEffect(() => {
    if (!campaign) return;
    if (!subjectLine) {
      if (campaign.subject_line) setSubjectLine(campaign.subject_line);
      else if (campaign.pipeline?.content?.output?.subject_line) {
        setSubjectLine(campaign.pipeline.content.output.subject_line);
      }
    }
    if (!fromName && campaign.from_name) setFromName(campaign.from_name);
    if (!replyTo && campaign.reply_to) setReplyTo(campaign.reply_to);
    if (!recipientListId && campaign.recipient_list_id) setRecipientListId(campaign.recipient_list_id);
    if (campaign.send_strategy) setSendStrategy(campaign.send_strategy);
  }, [campaign]); // eslint-disable-line react-hooks/exhaustive-deps

  // Determine if we have a completed campaign ready for finalization
  const isReadyToFinalize = htmlContent && (campaign?.status === 'review_needed' || campaign?.status === 'approved');

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden" data-testid={`campaign-tab-${campaignId}`}>
      {/* Top Header - Securado style */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-border/40 flex-shrink-0 bg-[hsl(var(--card)/0.5)]">
        <div className="flex items-center gap-4">
          {/* Campaign ID warm coral label */}
          <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-[hsl(var(--brand))]" data-testid="campaign-id-label">
            CAMPAIGN ID: {campaignId ? `CC-${campaignId.slice(-5).toUpperCase()}` : '...'}
          </span>

          {/* Pipeline status chips */}
          {campaign?.pipeline && (
            <div className="hidden md:flex items-center gap-1">
              {Object.entries(campaign.pipeline).map(([key, val]) => {
                if (typeof val !== 'object' || !val) return null;
                const meta = STEP_META[key];
                if (!meta) return null;
                const Icon = meta.icon;
                const isDone = val.status === 'success' || val.status === 'completed';
                return (
                  <div
                    key={key}
                    className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] ${
                      isDone ? 'text-[hsl(var(--status-success))]' : 'text-muted-foreground/50'
                    }`}
                  >
                    {isDone ? <CheckCircle2 className="w-3 h-3" /> : <Icon className="w-3 h-3" />}
                    <span className="hidden lg:inline">{meta.label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          {/* View mode toggle: Chat / Finalize */}
          {isReadyToFinalize && (
            <div className="flex items-center gap-0.5 bg-[hsl(var(--secondary)/0.3)] rounded-md p-0.5 mr-2">
              <button
                data-testid="campaign-view-chat"
                onClick={() => setViewMode('chat')}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors duration-150 ${
                  viewMode === 'chat' ? 'bg-[hsl(var(--card))] text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Chat
              </button>
              <button
                data-testid="campaign-view-finalize"
                onClick={() => setViewMode('finalize')}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors duration-150 ${
                  viewMode === 'finalize' ? 'bg-[hsl(var(--card))] text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Finalize
              </button>
            </div>
          )}
          {/* Pipeline Action Button - next to Chat/Finalize tabs */}
          {campaign?.status && !['draft', 'created', 'idle'].includes(campaign.status) && (
            <PipelineActionButton
              campaign={campaign}
              onApprove={handleApprove}
              onRevise={handleRevise}
              onFinalize={() => setViewMode('finalize')}
            />
          )}
        </div>
      </div>

      {/* ─── CHAT VIEW ─── */}
      {viewMode === 'chat' && (
        <ResizablePanelGroup direction="horizontal" className="flex-1" data-testid="campaign-editor-split">
          {/* LEFT PANE: Chat */}
          <ResizablePanel defaultSize={htmlContent ? 62 : 100} minSize={40} data-testid="campaign-editor-left-pane">
            <div className="flex flex-col h-full min-h-0">
              <ScrollArea className="flex-1 min-h-0" ref={chatScrollRef}>
                <div className="py-4 space-y-1">
                  {messages.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-20 text-center">
                      <Sparkles className="w-8 h-8 text-[hsl(var(--accent)/0.4)] mb-3" />
                      <p className="text-sm text-muted-foreground">
                        {campaign ? 'Campaign pipeline is running...' : 'Starting campaign...'}
                      </p>
                    </div>
                  )}
                  {messages.map((msg, idx) => (
                    <React.Fragment key={msg.id || idx}>
                      <ChatMsg msg={msg} onOpenPreview={() => {}} />
                      {/* Suggestion pills — only on last non-running bot message, context-aware */}
                      {msg.role !== 'user' && idx === messages.length - 1 && msg.status !== 'running' && (
                        <div className="flex flex-wrap gap-2 pl-14 pr-5 pt-1 pb-2" data-testid="chat-suggestion-pills">
                          {(() => {
                            const status = campaign?.status;
                            const pills = [];
                            if (status === 'content_review') {
                              pills.push({ label: 'Approve and continue pipeline', icon: ArrowRight, action: true });
                              pills.push({ label: 'Revise the brainstorm outline', icon: RefreshCw });
                            } else if (status === 'content_draft_review') {
                              pills.push({ label: 'Approve content and build HTML', icon: ArrowRight, action: true });
                              pills.push({ label: 'Revise the content draft', icon: RefreshCw });
                            } else if (status === 'review_needed') {
                              pills.push({ label: 'Approve the final campaign', icon: ArrowRight, action: true });
                              pills.push({ label: 'Request revisions', icon: RefreshCw });
                            } else if (status === 'pipeline_running') {
                              // No pills during active processing
                            } else if (status === 'approved' || status === 'sent') {
                              pills.push({ label: 'Show campaign summary', icon: Sparkles });
                            } else {
                              pills.push({ label: 'What is the current status?', icon: Sparkles });
                              pills.push({ label: 'Revise the campaign', icon: RefreshCw });
                            }
                            return pills.map(pill => (
                              <button
                                key={pill.label}
                                data-testid={`chat-pill-${pill.label.toLowerCase().replace(/\s/g, '-')}`}
                                onClick={() => {
                                  if (pill.action) {
                                    handleSend(pill.label);
                                  } else {
                                    setInput(pill.label);
                                  }
                                }}
                                className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors duration-150 ${
                                  pill.action
                                    ? 'border-[hsl(var(--brand)/0.3)] bg-[hsl(var(--brand)/0.1)] text-[hsl(var(--brand))] hover:bg-[hsl(var(--brand)/0.18)] hover:border-[hsl(var(--brand)/0.4)]'
                                    : 'border-white/10 bg-white/5 text-muted-foreground hover:bg-white/[0.08] hover:text-foreground hover:border-white/15'
                                }`}
                              >
                                <pill.icon className="w-3 h-3" />
                                {pill.label}
                              </button>
                            ));
                          })()}
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                  <div ref={scrollRef} />
                </div>
              </ScrollArea>
              <div className="border-t border-white/10 bg-[hsl(var(--pane)/0.8)] backdrop-blur flex-shrink-0" data-testid="chat-composer">
                {/* Dynamic status indicator */}
                <div className="flex items-center gap-2 px-5 pt-3 pb-1">
                  {(() => {
                    const st = campaign?.status;
                    if (st === 'content_review') return (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/35 bg-amber-500/12 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-amber-400" data-testid="chat-composer-status-indicator">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                        Awaiting Outline Approval
                      </span>
                    );
                    if (st === 'content_draft_review') return (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-violet-500/35 bg-violet-500/12 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-violet-400" data-testid="chat-composer-status-indicator">
                        <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
                        Awaiting Content Approval
                      </span>
                    );
                    if (st === 'review_needed') return (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--brand)/0.35)] bg-[hsl(var(--brand)/0.12)] px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[hsl(var(--brand))]" data-testid="chat-composer-status-indicator">
                        <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--brand))] animate-pulse" />
                        CXO Review Complete
                      </span>
                    );
                    if (st === 'pipeline_running') return (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--accent-warm)/0.35)] bg-[hsl(var(--accent-warm)/0.12)] px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[hsl(var(--accent-warm))]" data-testid="chat-composer-status-indicator">
                        <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--accent-warm))] animate-pulse" />
                        Pipeline Running
                      </span>
                    );
                    if (st === 'approved') return (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/35 bg-emerald-500/12 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-emerald-400" data-testid="chat-composer-status-indicator">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                        Approved
                      </span>
                    );
                    return (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--accent-warm)/0.35)] bg-[hsl(var(--accent-warm)/0.12)] px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[hsl(var(--accent-warm))]" data-testid="chat-composer-drafting-indicator">
                        <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--accent-warm))] animate-pulse" />
                        Drafting
                      </span>
                    );
                  })()}
                  <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">
                    {campaign?.topic ? campaign.topic.slice(0, 30) : 'Campaign'}
                  </span>
                </div>
                {/* Input row */}
                <div className="flex items-end gap-2 px-5 pb-2 pt-1">
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl text-muted-foreground hover:text-foreground" aria-label="Attach file" data-testid="chat-composer-attach">
                      <Paperclip className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl text-muted-foreground hover:text-foreground" aria-label="Add emoji" data-testid="chat-composer-emoji">
                      <SmilePlus className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl text-muted-foreground hover:text-foreground" aria-label="Voice input" data-testid="chat-composer-mic">
                      <Mic className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex-1 relative">
                    <Input
                      data-testid="chat-input"
                      placeholder="Type a message..."
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                      disabled={sending}
                      className="h-11 rounded-2xl bg-[hsl(var(--secondary)/0.4)] border-white/10 pr-4 text-sm"
                    />
                  </div>
                  <Button
                    data-testid="chat-send-btn"
                    onClick={() => handleSend()}
                    disabled={!input.trim() || sending}
                    className="h-10 w-10 rounded-full bg-[hsl(var(--accent-warm))] text-[hsl(var(--accent-warm-foreground))] hover:bg-[hsl(var(--accent-warm)/0.88)] flex-shrink-0 p-0"
                    aria-label="Send message"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
                {/* Keyboard hints */}
                <div className="flex items-center justify-center gap-4 px-5 pb-2.5 pt-0" data-testid="chat-composer-hints">
                  <span className="text-[10px] font-mono text-muted-foreground/50 tracking-wider">K FOR COMMANDS</span>
                  <span className="text-[10px] font-mono text-muted-foreground/50">•</span>
                  <span className="text-[10px] font-mono text-muted-foreground/50 tracking-wider">REFINE PROMPT</span>
                </div>
              </div>
            </div>
          </ResizablePanel>

          {/* RIGHT PANE: Preview */}
          {htmlContent && (
            <>
              <ResizableHandle withHandle className="bg-border/40 hover:bg-[hsl(var(--accent)/0.2)] transition-colors duration-150" />
              <ResizablePanel defaultSize={38} minSize={25} data-testid="campaign-editor-preview-pane">
                <PreviewPane
                  htmlContent={htmlContent}
                  previewMode={previewMode}
                  setPreviewMode={setPreviewMode}
                  previewDevice={previewDevice}
                  setPreviewDevice={setPreviewDevice}
                  publicUrl={publicUrl}
                  campaignId={campaignId}
                  campaign={campaign}
                  detailsOpen={detailsOpen}
                  setDetailsOpen={setDetailsOpen}
                  handleRevise={handleRevise}
                />
              </ResizablePanel>
            </>
          )}
        </ResizablePanelGroup>
      )}

      {/* ─── FINALIZE VIEW ─── */}
      {viewMode === 'finalize' && htmlContent && (
        <div className="flex flex-col flex-1 overflow-hidden">
          {/* Finalize Transmission Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 flex-shrink-0 bg-[hsl(var(--card)/0.5)]" data-testid="finalize-header">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--accent-warm))] font-semibold mb-1">Campaign Dispatch</p>
              <h1 className="text-2xl font-semibold tracking-tight text-foreground" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Finalize Transmission
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" className="h-10 px-5 rounded-xl border-white/10 bg-white/5 hover:bg-white/[0.08]" onClick={handleSaveDraft} disabled={savingDraft} data-testid="campaign-save-draft-btn">
                {savingDraft ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : 'Save Draft'}
              </Button>
              <Button className="h-10 px-5 gap-2 rounded-xl bg-[hsl(var(--accent-warm))] text-[hsl(var(--accent-warm-foreground))] hover:bg-[hsl(var(--accent-warm)/0.88)] active:scale-[0.98]" onClick={handleSendCampaign} disabled={sendingCampaign || !recipientListId} data-testid="campaign-send-now-btn">
                {sendingCampaign ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : <><Send className="w-4 h-4" /> Send Now</>}
              </Button>
            </div>
          </div>

          {/* Split pane: Preview + Config */}
          <ResizablePanelGroup direction="horizontal" className="flex-1" data-testid="campaign-finalize-split">
            {/* LEFT: Email Preview */}
            <ResizablePanel defaultSize={55} minSize={40} data-testid="finalize-preview-pane">
              <div className="flex flex-col h-full bg-[hsl(var(--pane))]">
                <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
                  <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-muted-foreground">EMAIL PREVIEW</span>
                  <div className="flex items-center gap-1">
                    <Button variant={previewDevice === 'desktop' ? 'secondary' : 'ghost'} size="icon" className="h-7 w-7" onClick={() => setPreviewDevice('desktop')}>
                      <Monitor className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant={previewDevice === 'mobile' ? 'secondary' : 'ghost'} size="icon" className="h-7 w-7" onClick={() => setPreviewDevice('mobile')}>
                      <Smartphone className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="flex-1 flex items-start justify-center overflow-auto bg-[hsl(var(--background))] p-4">
                  <div className={`h-full bg-white rounded-xl border border-white/10 overflow-hidden transition-[width] duration-200 ${previewDevice === 'mobile' ? 'w-[375px]' : 'w-full'}`} style={{ boxShadow: 'var(--shadow-elev-1)' }}>
                    {/* Browser chrome dots */}
                    <div className="flex items-center gap-1.5 px-3.5 py-2 bg-[hsl(var(--pane-2))] border-b border-white/10">
                      <span className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--destructive)/0.5)]" />
                      <span className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--status-warning)/0.5)]" />
                      <span className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--status-success)/0.5)]" />
                      <span className="ml-2 text-[10px] text-muted-foreground truncate flex-1 font-mono">email-preview</span>
                    </div>
                    <iframe srcDoc={htmlContent} title="Email Preview" className="w-full border-none" style={{ height: 'calc(100% - 34px)' }} sandbox="allow-same-origin" data-testid="finalize-preview-iframe" />
                  </div>
                </div>
                {/* Spam + Link badges + telemetry footer */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-white/10 bg-[hsl(var(--pane-2)/0.5)]">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5" data-testid="campaign-spam-score-badge">
                      <span className="w-2 h-2 rounded-full bg-[hsl(var(--status-success))]" />
                      <span className="text-xs text-muted-foreground">Spam Score:</span>
                      <span className="text-xs font-semibold text-[hsl(var(--status-success))]">0.4/10</span>
                      <span className="text-[10px] text-muted-foreground/60">Excellent</span>
                    </div>
                    <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5" data-testid="campaign-link-check-badge">
                      <span className="w-2 h-2 rounded-full bg-[hsl(var(--status-success))]" />
                      <span className="text-xs text-muted-foreground">Link Check:</span>
                      <span className="text-xs font-semibold text-foreground">4 Valid</span>
                      <span className="text-xs text-muted-foreground/60">0 Broken</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground">Est. Delivery</p>
                      <p className="text-sm font-semibold text-foreground" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                        {recipientListId ? (contactLists.find(l => l.id === recipientListId)?.contact_count ?? '—') : '—'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground">Latency</p>
                      <p className="text-sm font-semibold text-foreground" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>42ms</p>
                    </div>
                  </div>
                </div>
              </div>
            </ResizablePanel>

            <ResizableHandle withHandle className="bg-border/40 hover:bg-[hsl(var(--accent)/0.2)] transition-colors duration-150" />

            {/* RIGHT: Configuration Panel — Securado style */}
            <ResizablePanel defaultSize={45} minSize={30} data-testid="finalize-config-pane">
              <ScrollArea className="h-full">
                <div className="p-6 space-y-6" data-testid="campaign-finalize-config">

                  {/* ─── Configuration ─── */}
                  <div className="space-y-4">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--accent-warm))] font-semibold">Configuration</p>
                    <div className="space-y-4">
                      <div>
                        <label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium mb-2 block">Subject Line</label>
                        <Input
                          data-testid="campaign-subject-input"
                          value={subjectLine}
                          onChange={(e) => setSubjectLine(e.target.value)}
                          placeholder="Enter email subject line..."
                          className="h-11 rounded-xl bg-white/5 border-white/10 focus-visible:ring-[hsl(var(--accent-warm)/0.3)]"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium mb-2 block">From Name</label>
                          <Input
                            data-testid="campaign-from-name-input"
                            value={fromName}
                            onChange={(e) => setFromName(e.target.value)}
                            placeholder="Securado Security"
                            className="h-11 rounded-xl bg-white/5 border-white/10"
                          />
                        </div>
                        <div>
                          <label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium mb-2 block">Reply-To</label>
                          <Input
                            data-testid="campaign-reply-to-input"
                            value={replyTo}
                            onChange={(e) => setReplyTo(e.target.value)}
                            placeholder="reply@company.com"
                            className="h-11 rounded-xl bg-white/5 border-white/10"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium mb-2 block">Recipient List Selection</label>
                        <Select value={recipientListId} onValueChange={setRecipientListId} data-testid="campaign-recipient-list-select">
                          <SelectTrigger className="h-11 rounded-xl bg-white/5 border-white/10" data-testid="campaign-recipient-list-trigger">
                            <SelectValue placeholder="Select contact list..." />
                          </SelectTrigger>
                          <SelectContent>
                            {contactLists.length === 0 ? (
                              <SelectItem value="__none" disabled>No lists available — create one in Contacts</SelectItem>
                            ) : (
                              contactLists.map(list => (
                                <SelectItem key={list.id} value={list.id}>
                                  {list.name}{list.contact_count != null ? ` (${list.contact_count})` : ''}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* ─── Transmission Strategy — stacked cards ─── */}
                  <div className="space-y-3">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--accent-warm))] font-semibold">Transmission Strategy</p>
                    <div className="space-y-2">
                      <button
                        data-testid="campaign-strategy-now"
                        onClick={() => setSendStrategy('now')}
                        className={`w-full flex items-center gap-4 rounded-xl border p-4 text-left transition-colors duration-150 ${
                          sendStrategy === 'now'
                            ? 'border-[hsl(var(--accent-warm)/0.5)] bg-[hsl(var(--accent-warm)/0.06)]'
                            : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06]'
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          sendStrategy === 'now' ? 'bg-[hsl(var(--accent-warm)/0.15)]' : 'bg-white/5'
                        }`}>
                          <Zap className={`w-5 h-5 ${sendStrategy === 'now' ? 'text-[hsl(var(--accent-warm))]' : 'text-muted-foreground'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-foreground">Send Now</p>
                          <p className="text-xs text-muted-foreground mt-0.5">Dispatch to all recipients immediately</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      </button>
                      <button
                        data-testid="campaign-strategy-schedule"
                        onClick={() => setSendStrategy('schedule')}
                        className={`w-full flex items-center gap-4 rounded-xl border p-4 text-left transition-colors duration-150 ${
                          sendStrategy === 'schedule'
                            ? 'border-[hsl(var(--accent-warm)/0.5)] bg-[hsl(var(--accent-warm)/0.06)]'
                            : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06]'
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          sendStrategy === 'schedule' ? 'bg-[hsl(var(--accent-warm)/0.15)]' : 'bg-white/5'
                        }`}>
                          <Clock className={`w-5 h-5 ${sendStrategy === 'schedule' ? 'text-[hsl(var(--accent-warm))]' : 'text-muted-foreground'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-foreground">Schedule for Later</p>
                          <p className="text-xs text-muted-foreground mt-0.5">Choose a specific date and time</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      </button>
                    </div>
                  </div>

                  {/* ─── Final Pre-Flight Check ─── */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--accent-warm))] font-semibold">Final Pre-Flight Check</p>
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--status-success)/0.25)] bg-[hsl(var(--status-success)/0.1)] px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--status-success))]" data-testid="preflight-ready-badge">
                        <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--status-success))]" />
                        Ready
                      </span>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-white/[0.03] divide-y divide-white/5">
                      <PreflightRow label="SPF / DKIM / DMARC" status="pass" detail="All records verified" />
                      <PreflightRow label="Dynamic Tags" status="pass" detail="All tags mapped" />
                      <PreflightRow label="CXO Review" status={campaign?.pipeline?.review?.output?.approved ? 'pass' : 'warn'} detail={`Score: ${campaign?.pipeline?.review?.output?.overall_score || 'N/A'}/100`} />
                      <PreflightRow label="A/B Test" status="neutral" detail="Not configured" />
                    </div>
                  </div>

                  {/* ─── Telemetry Footer ─── */}
                  <div className="rounded-xl border border-white/10 bg-white/[0.03] px-5 py-4 flex items-center justify-between" data-testid="campaign-telemetry-strip">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">Est. Delivery</p>
                      <p className="text-xl font-semibold text-foreground" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                        {recipientListId ? (contactLists.find(l => l.id === recipientListId)?.contact_count ?? '—') : '—'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">Latency</p>
                      <p className="text-xl font-semibold text-foreground" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>42ms</p>
                    </div>
                  </div>
                </div>
              </ScrollArea>
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      )}

      {/* Revision Dialog */}
      <Dialog open={showReviseDialog} onOpenChange={setShowReviseDialog}>
        <DialogContent className="max-w-lg" data-testid="revise-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-[hsl(var(--brand))]" />
              Request Revision
            </DialogTitle>
            <DialogDescription>
              Tell us what you want changed. Choose which stages to re-run.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {/* Scope selector */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Revision Scope</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'full', label: 'Full Rewrite', desc: 'Content + HTML + Review' },
                  { id: 'html', label: 'HTML Only', desc: 'Rebuild formatting' },
                  { id: 'review', label: 'Re-Review', desc: 'CXO review only' },
                ].map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => setReviseTarget(opt.id)}
                    data-testid={`revise-scope-${opt.id}`}
                    className={`text-left p-2.5 rounded-lg border transition-colors duration-150 ${
                      reviseTarget === opt.id
                        ? 'border-[hsl(var(--brand)/0.4)] bg-[hsl(var(--brand)/0.1)] text-[hsl(var(--brand))]'
                        : 'border-white/10 bg-white/[0.03] text-muted-foreground hover:bg-white/[0.06]'
                    }`}
                  >
                    <div className="text-xs font-semibold">{opt.label}</div>
                    <div className="text-[10px] opacity-70">{opt.desc}</div>
                  </button>
                ))}
              </div>
            </div>
            {/* Quick suggestions */}
            <div className="grid grid-cols-3 gap-2">
              {['Change the tone to be more urgent', 'Make the CTA stronger', 'Simplify the language'].map(suggestion => (
                <button
                  key={suggestion}
                  onClick={() => setReviseFeedback(prev => prev ? `${prev}. ${suggestion}` : suggestion)}
                  className="text-xs text-left p-2.5 rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-[hsl(var(--brand)/0.2)] transition-colors duration-150 text-muted-foreground"
                >
                  {suggestion}
                </button>
              ))}
            </div>
            <Textarea
              value={reviseFeedback}
              onChange={(e) => setReviseFeedback(e.target.value)}
              placeholder="Describe the changes you want (e.g., 'Make it more concise', 'Add statistics about ransomware costs', 'Change the subject line to be more action-oriented')"
              rows={4}
              className="resize-none"
              data-testid="revise-feedback-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowReviseDialog(false); setReviseFeedback(''); setReviseTarget('full'); }}>Cancel</Button>
            <Button
              onClick={() => handleRevise(reviseFeedback || 'Please improve the campaign.')}
              className="bg-[hsl(var(--accent-warm))] hover:bg-[hsl(var(--accent-warm)/0.88)] text-[hsl(var(--accent-warm-foreground))] btn-press"
              data-testid="revise-submit-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" /> Submit Revision
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
function PreviewPane({ htmlContent, previewMode, setPreviewMode, previewDevice, setPreviewDevice, publicUrl, campaignId, campaign, detailsOpen, setDetailsOpen, handleRevise }) {
  return (
    <div className="flex flex-col h-full bg-[hsl(var(--pane))]">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border/40 flex-shrink-0" data-testid="campaign-preview-toolbar">
        <div className="flex items-center gap-1 bg-[hsl(var(--secondary)/0.3)] rounded-md p-0.5">
          <button data-testid="campaign-preview-mode-preview" onClick={() => setPreviewMode('preview')}
            className={`px-2.5 py-1.5 rounded text-xs font-medium transition-colors duration-150 ${previewMode === 'preview' ? 'bg-[hsl(var(--card))] text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
          >Preview</button>
          <button data-testid="campaign-preview-mode-code" onClick={() => setPreviewMode('code')}
            className={`px-2.5 py-1.5 rounded text-xs font-medium transition-colors duration-150 ${previewMode === 'code' ? 'bg-[hsl(var(--card))] text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
          >Code</button>
        </div>
        <TooltipProvider delayDuration={200}>
          <div className="flex items-center gap-1">
            {previewMode === 'preview' && (
              <>
                <Tooltip><TooltipTrigger asChild><Button variant={previewDevice === 'desktop' ? 'secondary' : 'ghost'} size="icon" className="h-7 w-7" onClick={() => setPreviewDevice('desktop')} data-testid="campaign-preview-device-desktop"><Monitor className="w-3.5 h-3.5" /></Button></TooltipTrigger><TooltipContent className="text-xs">Desktop</TooltipContent></Tooltip>
                <Tooltip><TooltipTrigger asChild><Button variant={previewDevice === 'mobile' ? 'secondary' : 'ghost'} size="icon" className="h-7 w-7" onClick={() => setPreviewDevice('mobile')} data-testid="campaign-preview-device-mobile"><Smartphone className="w-3.5 h-3.5" /></Button></TooltipTrigger><TooltipContent className="text-xs">Mobile</TooltipContent></Tooltip>
              </>
            )}
            {previewMode === 'code' && (
              <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { navigator.clipboard.writeText(htmlContent); toast.success('HTML copied'); }} data-testid="campaign-preview-copy-code"><Copy className="w-3.5 h-3.5" /></Button></TooltipTrigger><TooltipContent className="text-xs">Copy HTML</TooltipContent></Tooltip>
            )}
            <div className="w-px h-4 bg-border/40 mx-1" />
            <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleRevise} data-testid="campaign-preview-regenerate"><RefreshCw className="w-3.5 h-3.5" /></Button></TooltipTrigger><TooltipContent className="text-xs">Regenerate</TooltipContent></Tooltip>
            <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDetailsOpen(true)} data-testid="campaign-preview-details"><Info className="w-3.5 h-3.5" /></Button></TooltipTrigger><TooltipContent className="text-xs">Details</TooltipContent></Tooltip>
            {publicUrl && (
              <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => window.open(publicUrl, '_blank')} data-testid="campaign-preview-open-external"><ExternalLink className="w-3.5 h-3.5" /></Button></TooltipTrigger><TooltipContent className="text-xs">Open in new tab</TooltipContent></Tooltip>
            )}
          </div>
        </TooltipProvider>
      </div>
      {previewMode === 'preview' ? (
        <div className="flex-1 flex items-start justify-center overflow-auto bg-[hsl(var(--background))] p-3">
          <div className={`h-full bg-white rounded-lg border border-border/40 overflow-hidden shadow-sm transition-[width] duration-200 ${previewDevice === 'mobile' ? 'w-[375px]' : 'w-full'}`}>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[hsl(var(--secondary)/0.5)] border-b border-border/40">
              <span className="w-2 h-2 rounded-full bg-[hsl(var(--destructive)/0.4)]" /><span className="w-2 h-2 rounded-full bg-[hsl(var(--status-warning)/0.4)]" /><span className="w-2 h-2 rounded-full bg-[hsl(var(--status-success)/0.4)]" />
              <span className="ml-2 text-[10px] text-muted-foreground truncate flex-1">email-preview</span>
            </div>
            <iframe srcDoc={htmlContent} title="Email Preview" className="w-full border-none" style={{ height: 'calc(100% - 28px)' }} sandbox="allow-same-origin" data-testid="campaign-preview-iframe" />
          </div>
        </div>
      ) : (
        <ScrollArea className="flex-1" data-testid="campaign-preview-code-view">
          <pre className="p-4 text-xs font-mono text-foreground/80 leading-relaxed whitespace-pre-wrap break-all"><code>{htmlContent}</code></pre>
        </ScrollArea>
      )}
      <Sheet open={detailsOpen} onOpenChange={setDetailsOpen}>
        <SheetContent className="w-[360px] sm:w-[420px]" data-testid="campaign-details-sheet">
          <SheetHeader><SheetTitle className="text-base">Campaign Details</SheetTitle></SheetHeader>
          {campaign && (
            <div className="mt-4 space-y-4">
              <DetailRow label="Campaign ID" value={campaign.id} mono />
              <DetailRow label="Topic" value={campaign.topic || campaign.name || 'Untitled'} />
              <DetailRow label="Material Type" value={(campaign.material_type || '').replace(/_/g, ' ')} />
              <DetailRow label="Status" value={campaign.status || 'draft'} />
              <DetailRow label="Created" value={campaign.created_at ? new Date(campaign.created_at).toLocaleString() : 'N/A'} />
              {publicUrl && (<div><p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Public URL</p><code className="text-[11px] text-muted-foreground break-all">{publicUrl}</code></div>)}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ─── PreflightRow helper ────────────────────────────────────────────
function PreflightRow({ label, status, detail }) {
  const color = status === 'pass' ? 'hsl(var(--status-success))' : status === 'warn' ? 'hsl(var(--status-warning))' : 'hsl(var(--muted-foreground))';
  const icon = status === 'pass' ? <CheckCircle2 className="w-4 h-4" style={{ color }} /> : status === 'warn' ? <AlertTriangle className="w-4 h-4" style={{ color }} /> : <div className="w-4 h-4 rounded-full border-2 border-muted-foreground/30" />;
  return (
    <div className="flex items-center justify-between px-4 py-3" data-testid={`preflight-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
      <div className="flex items-center gap-2.5">
        {icon}
        <span className="text-sm font-medium text-foreground">{label}</span>
      </div>
      <span className="text-xs text-muted-foreground">{detail}</span>
    </div>
  );
}

// ─── Detail Row helper ─────────────────────────────────────────────
function DetailRow({ label, value, mono }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">{label}</p>
      <p className={`text-sm text-foreground ${mono ? 'font-mono text-xs' : ''}`}>{value || 'N/A'}</p>
    </div>
  );
}
